import { useState, useEffect, useCallback } from 'react';

import { MAPBOX_ACCESS_TOKEN } from '@m/config/env';

interface PositionState {
  accuracy: number | null;
  latitude: number | null;
  longitude: number | null;
}

interface LocationAddress {
  city: string;
  postcode: string;
  state: string;
}

interface LocationData {
  address: LocationAddress;
}

interface GeolocationOptions {
  enableHighAccuracy?: boolean;
  maximumAge?: number;
  timeout?: number;
}

const useLocation = ({
  options = {
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0,
  },
  onError,
  trigger,
}: {
  onError?: (message: string) => void;
  options?: GeolocationOptions;
  // Fetch the browser location when this flips to true. Consumers that should not
  // grab the current user's position (e.g. survey forms that pre-populate coordinates)
  // pass false and call requestLocation() explicitly instead.
  trigger: boolean;
}) => {
  const [position, setPosition] = useState<PositionState>({
    latitude: null,
    longitude: null,
    accuracy: null,
  });
  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const [errors, setErrors] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  // Tracks the geolocation permission only to drive the denied banner. Fetching is
  // never started here — that would override coordinates the consumer already holds.
  useEffect(() => {
    if (!navigator.permissions) return () => {};
    let status: PermissionStatus | null | undefined;
    const handleChange = () => {
      if (!status) return;
      if (status.state === 'denied') setPermissionDenied(true);
      // 'prompt' means user reset permission in Settings — safe to clear the denied banner.
      // 'granted' is intentionally skipped: iOS can misreport it, so getCurrentPosition
      // success remains the authoritative signal that clears permissionDenied.
      else if (status.state === 'prompt') setPermissionDenied(false);
    };
    navigator.permissions
      .query({ name: 'geolocation' })
      .then((result) => {
        if (status === null) return;
        status = result;
        result.addEventListener('change', handleChange);
        // Already denied in a previous session — show the banner immediately.
        if (result.state === 'denied') setPermissionDenied(true);
      })
      .catch(() => {});
    return () => {
      status?.removeEventListener('change', handleChange);
      status = null; // guard late Promise resolutions and signal cleanup
    };
  }, []);

  const fetchLocation = useCallback(() => {
    if ('geolocation' in navigator) {
      setErrors(null);
      setPermissionDenied(false);
      setIsFetching(true);
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          setIsFetching(false);
          // Override any stale 'denied' state from the permissions API (iOS Safari quirk)
          setPermissionDenied(false);
          const crd = pos.coords;

          setPosition({
            latitude: crd.latitude,
            longitude: crd.longitude,
            accuracy: crd.accuracy,
          });

          const mapboxUrl =
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${crd.longitude},${crd.latitude}.json` +
            `?access_token=${MAPBOX_ACCESS_TOKEN}`;

          try {
            const response = await fetch(mapboxUrl);
            if (!response.ok) throw new Error('Failed to fetch location data');
            const data = await response.json();
            const context: { id: string; text: string }[] = data.features?.[0]?.context ?? [];
            const city = context.find(({ id }) => id.startsWith('place.'))?.text ?? '';
            const state = context.find(({ id }) => id.startsWith('region.'))?.text ?? '';
            const postcode = context.find(({ id }) => id.startsWith('postcode.'))?.text ?? '';
            setLocationData({ address: { city, state, postcode } });
          } catch (fetchError: any) {
            const message = 'Could not fetch location address';
            setErrors(message);
            onError?.(message);
          }
        },
        (err) => {
          setIsFetching(false);
          if (err.code === 1 /* PERMISSION_DENIED */) {
            setPermissionDenied(true);
          }
          const message = `ERROR(${err.code}): ${err.message}`;
          setErrors(message);
          onError?.(message);
        },
        {
          enableHighAccuracy: options.enableHighAccuracy ?? true,
          timeout: options.timeout ?? 5000,
          maximumAge: options.maximumAge ?? 0,
        },
      );
    } else {
      setIsFetching(false);
      const message = 'Geolocation is not supported by this browser.';
      setErrors(message);
      onError?.(message);
    }
  }, [onError, options.enableHighAccuracy, options.timeout, options.maximumAge]);

  useEffect(() => {
    if (trigger) {
      // Only run when trigger changes to true
      fetchLocation();
    }
  }, [trigger, fetchLocation]);

  return {
    position,
    locationData,
    errors,
    permissionDenied,
    isFetching,
    requestLocation: fetchLocation,
  };
};

export default useLocation;
